import { addHours, isToday } from 'date-fns'

type Availability = {
  day: string
  hours: string[]
}

const TIME_RANGE_SEPARATOR_REGEX = /\s*[—–-]\s*/

const formatTimeFromDate = (date: Date) =>
  `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`

const normalizeTimeString = (time: string): string | null => {
  const match = time.trim().match(/^(\d{1,2}):(\d{1,2})$/)
  if (!match) {
    return null
  }

  const [, hours, minutes] = match
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`
}

const parseTimeRange = (entry: string): [string, string] | null => {
  const [startTime, endTime, ...extraParts] = entry.split(TIME_RANGE_SEPARATOR_REGEX)
  if (extraParts.length > 0 || !startTime || !endTime) {
    return null
  }

  const normalizedStart = normalizeTimeString(startTime)
  const normalizedEnd = normalizeTimeString(endTime)

  if (!normalizedStart || !normalizedEnd) {
    return null
  }

  return [normalizedStart, normalizedEnd]
}

export const getDateTime = (time: string, date: Date) => {
  const [hours, minutes] = time.split(':')
  return new Date(new Date(date).setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0))
}

const WEEK_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/**
 * Since the hours array can contain ending hours past midnight,
 * we can correct the array to contain hours just inside a 24h framework
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
const correctAvailableHours = (_availability: Availability[]): Availability[] => {
  const availability: Availability[] = _availability.map((x) => ({
    day: x.day,
    hours: [...x.hours],
  }))

  for (let index = 0; index < availability.length; index++) {
    const prevDayPickIndex = index - 1 >= 0 ? index - 1 : availability.length - 1
    const prev = availability[prevDayPickIndex]
    const current = availability[index]

    const afterMidnightEntry = prev?.hours.find((entry) => {
      const parsedTimeRange = parseTimeRange(entry)
      if (!parsedTimeRange) {
        return false
      }

      const [start, end] = parsedTimeRange
      return start > end && start < '24:00' && end >= '00:00' && end <= '06:00'
    })
    const currentDayIndex = WEEK_DAYS.indexOf(current?.day)
    const prevDayIndex = WEEK_DAYS.indexOf(prev?.day)
    const consecutiveDays =
      currentDayIndex - prevDayIndex === 1 ||
      (currentDayIndex === 0 && prevDayIndex === WEEK_DAYS.length - 1)

    if (afterMidnightEntry && consecutiveDays) {
      const parsedTimeRange = parseTimeRange(afterMidnightEntry)
      if (parsedTimeRange) {
        const [start, end] = parsedTimeRange
        // Keep a midnight spillover selectable at 00:00 without representing it as the
        // 00:00-00:00 closed-day sentinel.
        const newInterval = `00:00 — ${end === '00:00' ? '00:01' : end}`
        if (end && current.hours.indexOf(newInterval) === -1) {
          current.hours.push(newInterval)
        }
        const afterMidnightEntryIndex = prev.hours.indexOf(afterMidnightEntry)
        if (start !== '00:00') {
          prev.hours[afterMidnightEntryIndex] = `${start} — 24:00`
        }
      }
    }
  }
  return availability
}

export const isBefore = (date1: Date, date2: Date) => {
  return date1.getTime() <= date2.getTime()
}

/**
 * Util function to check if a given time is between available hours.
 * If a minHour is provided, it will also check if the time is greater than the minHour.
 */
export const checkDisabledTime = (
  _locale: string,
  _chosenTime: string,
  _chosenDate?: Date,
  _availability?: Availability[],
  _minDateTime?: Date,
  _isLastAvailableRentalDay?: boolean
) => {
  if (!_chosenDate || !_availability) {
    return false
  }

  const availability = correctAvailableHours(_availability)

  const now = new Date()
  // Always use en-US locale to get English day names for comparison with availability data
  const chosenDayName = _chosenDate.toLocaleDateString('en-US', { weekday: 'long' })
  const chosenDay = availability.find((x) => x.day === chosenDayName)

  const chosenDateTime = getDateTime(_chosenTime, _chosenDate)

  const getEndDateTime = (endTime: string) => {
    if (!_isLastAvailableRentalDay) {
      return getDateTime(endTime, _chosenDate)
    }
    const minTime = _minDateTime ? formatTimeFromDate(_minDateTime) : null
    return minTime ? getDateTime(minTime, _chosenDate) : getDateTime(endTime, _chosenDate)
  }

  const isInAvailableHours = !!chosenDay?.hours?.some((x) => {
    const parsedTimeRange = parseTimeRange(x)
    if (!parsedTimeRange) {
      return false
    }
    const [startTime, endTime] = parsedTimeRange
    const startDateTime = getDateTime(startTime, _chosenDate)
    const endDateTime = getEndDateTime(endTime)
    return (
      startTime !== endTime &&
      isBefore(startDateTime, chosenDateTime) &&
      isBefore(chosenDateTime, endDateTime)
    )
  })

  let minDateTime
  if (_minDateTime) {
    // It must be at least 1 hour later than the minimum time
    minDateTime = addHours(new Date(_minDateTime), 1)
  }

  return !(
    isInAvailableHours &&
    (minDateTime ? isBefore(minDateTime, chosenDateTime) : true) &&
    (isToday(_chosenDate as Date) ? isBefore(now, chosenDateTime) : true)
  )
}
